/**
 * Tabela da lista administrativa (`/admin/cartoes`), no mesmo espírito de
 * CertameTable: `<table>` com tokens. Abaixo de `sm`, as mesmas colunas
 * empilham numa ficha. Os rótulos vêm de `cartaoLinha` e `cardTipo`.
 */
import { Popconfirm } from 'antd';
import { Pencil, Trash2 } from 'lucide-react';
import type { ReviewCardAdminRow } from '../../../sdk/@types/Revisao';
import { cardTipoMostrado } from './cardTipo';
import {
  accuracyLabel,
  markingCount,
  markingLabel,
  shortIdLabel,
  updatedLabel,
} from './cartaoLinha';
import DuplicadoBadge from './DuplicadoBadge';
import type { SelecaoLinhas } from './loteSelecao';

export interface CartoesTableProps {
  rows: ReviewCardAdminRow[];
  /** Nome da matéria; undefined quando o id não está no catálogo. */
  subjectName: (subjectId: string) => string | undefined;
  loading: boolean;
  /** Quando não há linhas e não está carregando. Não é erro. */
  emptyLabel: string;
  onEdit: (card: ReviewCardAdminRow) => void;
  onDelete: (id: string) => void;
  /** Conjunto de cartões marcados. A view é dona do set; a tabela só liga e desliga. */
  selecao: SelecaoLinhas;
}

const NEUTRAL_CHIP =
  'rounded-chip bg-surface-muted px-2 py-0.5 text-xs text-ink-secondary';

const EDIT_UNLABELED =
  'inline-flex h-8 w-8 items-center justify-center rounded-input text-ink-secondary hover:bg-surface-hover hover:text-ink';

const EDIT_LABELED =
  'inline-flex items-center gap-1.5 rounded-input border border-edge bg-surface px-3 py-1.5 text-sm font-medium text-ink hover:bg-surface-hover';

const DELETE_UNLABELED =
  'inline-flex h-8 w-8 items-center justify-center rounded-input text-danger-ink hover:bg-danger/[0.08]';

const DELETE_LABELED =
  'inline-flex items-center gap-1.5 rounded-input border border-danger/40 bg-surface px-3 py-1.5 text-sm font-medium text-danger-ink hover:bg-danger/[0.08]';

const CELL = 'px-3 py-3 align-top';

const HEADER_CELL = 'px-3 py-2 text-left text-xs font-semibold text-ink-secondary';

/** Shares of the 880px floor. The leading column is the row checkbox.
 *  Cartão gives up that share so the front can still truncate inside the cell.
 *  Ações stays wide enough for the two 32px icon buttons. */
const COLUMNS: { label: string; width: string; srOnly?: boolean }[] = [
  { label: 'Selecionar', width: '4%', srOnly: true },
  { label: 'Cartão', width: '17%' },
  { label: 'Tipo', width: '12%' },
  { label: 'Matéria', width: '14%' },
  { label: 'Tags', width: '14%' },
  { label: 'Acerto', width: '14%' },
  { label: 'Atualizado', width: '13%' },
  { label: 'Ações', width: '12%' },
];

function frontOf(card: ReviewCardAdminRow): { text: string; title?: string } {
  const trimmed = card.front?.trim() ?? '';
  if (!trimmed) return { text: '—' };
  return { text: trimmed, title: trimmed };
}

function backOf(card: ReviewCardAdminRow): string | null {
  const trimmed = card.back?.trim() ?? '';
  return trimmed || null;
}

function marcacoes(card: ReviewCardAdminRow): string | null {
  const count = markingCount(card);
  if (count === null) return null;
  return markingLabel(count);
}

function nomeMateria(subjectName: CartoesTableProps['subjectName'], subjectId: string): string {
  return subjectName(subjectId) || '—';
}

function AccuracyText({ card }: { card: ReviewCardAdminRow }) {
  const label = accuracyLabel(card);
  return (
    <span className={label === 'Indisponível' ? 'text-ink-muted' : 'text-ink'}>{label}</span>
  );
}

function TagChips({ tags }: { tags: string[] | undefined }) {
  if (!tags?.length) return <span>—</span>;
  return (
    <>
      {tags.map((tag, index) => (
        <span key={`${tag}-${index}`} className={`${NEUTRAL_CHIP} max-w-full break-words`}>
          {tag}
        </span>
      ))}
    </>
  );
}

function RowActions({
  card,
  labeled,
  onEdit,
  onDelete,
}: {
  card: ReviewCardAdminRow;
  labeled: boolean;
  onEdit: CartoesTableProps['onEdit'];
  onDelete: CartoesTableProps['onDelete'];
}) {
  return (
    <div className={`flex items-center gap-2 ${labeled ? 'flex-wrap' : 'flex-nowrap'}`}>
      <button
        type="button"
        className={labeled ? EDIT_LABELED : EDIT_UNLABELED}
        onClick={() => onEdit(card)}
      >
        <Pencil size={16} aria-hidden />
        {labeled ? 'Editar' : <span className="sr-only">Editar</span>}
      </button>
      <Popconfirm
        title="Excluir cartão?"
        description="Remove o template e a cópia de todos os alunos."
        okText="Excluir"
        cancelText="Cancelar"
        okButtonProps={{ danger: true }}
        onConfirm={() => onDelete(card.id)}
      >
        <button type="button" className={labeled ? DELETE_LABELED : DELETE_UNLABELED}>
          <Trash2 size={16} aria-hidden />
          {labeled ? 'Excluir' : <span className="sr-only">Excluir</span>}
        </button>
      </Popconfirm>
    </div>
  );
}

function checkboxLabel(card: ReviewCardAdminRow): string {
  return `Selecionar ${shortIdLabel(card.shortId) ?? frontOf(card).text}`;
}

function RowCheckbox({
  card,
  selecao,
  roomy = false,
}: {
  card: ReviewCardAdminRow;
  selecao: SelecaoLinhas;
  /** Alvo de toque na ficha estreita. No desktop a célula já cabe o controle. */
  roomy?: boolean;
}) {
  const input = (
    <input
      type="checkbox"
      checked={selecao.selectedIds.has(card.id)}
      onChange={() => selecao.onToggle(card.id)}
      onClick={(event) => event.stopPropagation()}
      aria-label={checkboxLabel(card)}
      className={roomy ? 'h-5 w-5' : 'h-4 w-4'}
    />
  );
  if (!roomy) return input;
  return (
    <label
      className="inline-flex h-10 w-10 shrink-0 items-center justify-center"
      onClick={(event) => event.stopPropagation()}
    >
      {input}
    </label>
  );
}

function CartaoConteudo({ card }: { card: ReviewCardAdminRow }) {
  const front = frontOf(card);
  const back = backOf(card);
  const markings = marcacoes(card);
  return (
    <div className="min-w-0">
      <div className="flex min-w-0 items-baseline gap-2">
        <span className="shrink-0 text-ink-muted">{shortIdLabel(card.shortId) ?? '—'}</span>
        <span className="min-w-0 truncate" title={front.title}>
          {front.text}
        </span>
      </div>
      {back ? (
        <div className="min-w-0 truncate text-xs text-ink-muted" title={back}>
          {back}
        </div>
      ) : null}
      {markings !== null ? <div className="text-xs text-ink-muted">{markings}</div> : null}
      {card.probableDuplicate ? (
        <div className="mt-1 min-w-0">
          <DuplicadoBadge duplicate={card.probableDuplicate} />
        </div>
      ) : null}
    </div>
  );
}

function DesktopTable({
  rows,
  subjectName,
  onEdit,
  onDelete,
  selecao,
}: Omit<CartoesTableProps, 'loading' | 'emptyLabel'>) {
  return (
    <div className="hidden overflow-x-auto sm:block">
      <div className="min-w-[880px] overflow-hidden rounded-card border border-edge bg-surface">
        <table className="w-full table-fixed border-collapse text-left text-sm text-ink">
          <colgroup>
            {COLUMNS.map((column) => (
              <col key={column.label} style={{ width: column.width }} />
            ))}
          </colgroup>
          <thead>
            <tr className="bg-surface-muted">
              {COLUMNS.map((column) => (
                <th
                  key={column.label}
                  scope="col"
                  className={column.srOnly ? 'px-1 py-2 text-left' : HEADER_CELL}
                >
                  {column.srOnly ? (
                    <span className="sr-only">{column.label}</span>
                  ) : (
                    column.label
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((card) => {
              const selected = selecao.selectedIds.has(card.id);
              return (
                <tr
                  key={card.id}
                  className={`border-t border-edge ${selected ? 'bg-select/[0.14]' : 'bg-surface hover:bg-surface-hover'}`}
                >
                  <td className="px-1 py-3 align-middle">
                    <RowCheckbox card={card} selecao={selecao} />
                  </td>
                  <td className={`${CELL} overflow-hidden`}>
                    <CartaoConteudo card={card} />
                  </td>
                  <td className={CELL}>
                    <span className={NEUTRAL_CHIP}>{cardTipoMostrado(card)}</span>
                  </td>
                  <td className={`${CELL} overflow-hidden`}>
                    <div className="min-w-0 break-words">
                      {nomeMateria(subjectName, card.subjectId)}
                    </div>
                  </td>
                  <td className={`${CELL} overflow-hidden`}>
                    <div className="flex min-w-0 flex-wrap gap-1">
                      <TagChips tags={card.tags} />
                    </div>
                  </td>
                  <td className={CELL}>
                    <AccuracyText card={card} />
                  </td>
                  <td className={`${CELL} text-ink-secondary`}>{updatedLabel(card.updatedAt)}</td>
                  <td className={CELL}>
                    <RowActions card={card} labeled={false} onEdit={onEdit} onDelete={onDelete} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function NarrowList({
  rows,
  subjectName,
  onEdit,
  onDelete,
  selecao,
}: Omit<CartoesTableProps, 'loading' | 'emptyLabel'>) {
  return (
    <div className="flex min-w-0 flex-col gap-3 sm:hidden">
      {rows.map((card) => {
        const front = frontOf(card);
        const back = backOf(card);
        const markings = marcacoes(card);
        return (
          <article
            key={card.id}
            className={`flex min-w-0 flex-col gap-2 rounded-card border border-edge p-3 text-sm text-ink ${selecao.selectedIds.has(card.id) ? 'bg-select/[0.14]' : 'bg-surface'}`}
          >
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex min-w-0 items-center gap-1">
                <RowCheckbox card={card} selecao={selecao} roomy />
                <span className="text-ink-muted">{shortIdLabel(card.shortId) ?? '—'}</span>
              </div>
              <span className={NEUTRAL_CHIP}>{cardTipoMostrado(card)}</span>
            </div>
            <div className="min-w-0">
              <div className="min-w-0 truncate" title={front.title}>
                {front.text}
              </div>
              {back ? (
                <div className="min-w-0 truncate text-xs text-ink-muted" title={back}>
                  {back}
                </div>
              ) : null}
            </div>
            {markings !== null ? <div className="text-xs text-ink-muted">{markings}</div> : null}
            {card.probableDuplicate ? (
              <div className="min-w-0">
                <DuplicadoBadge duplicate={card.probableDuplicate} />
              </div>
            ) : null}
            <div>
              <span className="text-ink-muted">Matéria </span>
              <span className="break-words">{nomeMateria(subjectName, card.subjectId)}</span>
            </div>
            <div className="flex min-w-0 flex-wrap items-center gap-1">
              <span className="text-ink-muted">Tags</span>
              <TagChips tags={card.tags} />
            </div>
            <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
              <div>
                <span className="text-ink-muted">Acerto </span>
                <AccuracyText card={card} />
              </div>
              <div>
                <span className="text-ink-muted">Atualizado </span>
                <span className="text-ink-secondary">{updatedLabel(card.updatedAt)}</span>
              </div>
            </div>
            <RowActions card={card} labeled onEdit={onEdit} onDelete={onDelete} />
          </article>
        );
      })}
    </div>
  );
}

export default function CartoesTable(props: CartoesTableProps): JSX.Element {
  const { rows, subjectName, loading, emptyLabel, onEdit, onDelete, selecao } = props;
  const linha = { rows, subjectName, onEdit, onDelete, selecao };

  return (
    <div aria-busy={loading}>
      {rows.length === 0 ? (
        <p className="text-sm text-ink-muted">{loading ? 'Carregando…' : emptyLabel}</p>
      ) : (
        <>
          <DesktopTable {...linha} />
          <NarrowList {...linha} />
        </>
      )}
    </div>
  );
}
